import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Button, Card, DatePicker, Spin } from 'antd';
import { DownloadOutlined, ReloadOutlined, FileTextOutlined, DollarOutlined, CheckCircleOutlined, ExclamationCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import ReactEcharts from 'echarts-for-react';
import classNames from 'classnames';
import { IAppComponentProps } from '../../../../../components';
import { getI18n } from '../../../../../utils/Utils';
import { QUERY_CFO_REPORTING } from '../CFOReportingDashboard/cfoReportingQueries';
import { EIM_THEME_STORAGE_KEY } from '../../../themePreference';
import styles from './cfoComplianceDashboard.module.scss';
import { getRangePickerLocalization } from '../../../../info-portal/components/grid/CalendarUtils';
import { escapeHtml, getCountryFlagUrl } from '../../eimDashboardUtils';

const { RangePicker } = DatePicker;

type ThemeMode = 'dark' | 'light';

type CfoMetric = {
    label?: string | null;
    value?: number | null;
    delta?: number | null;
    percentageChange?: number | null;
};

type CfoTrendRow = {
    date?: string | null;
    invoiceCount?: number | null;
    previousInvoiceCount?: number | null;
    totalAmount?: number | null;
};

type CfoStatusRow = {
    status?: string | null;
    count?: number | null;
    percentage?: number | null;
};

type CfoCountryRow = {
    country?: string | null;
    invoiceCount?: number | null;
    percentage?: number | null;
};

type CfoCurrencyRow = {
    currency?: string | null;
    currentAmount?: number | null;
    previousAmount?: number | null;
    delta?: number | null;
    percentageChange?: number | null;
};

type CfoErrorReason = {
    reason?: string | null;
    count?: number | null;
    percentage?: number | null;
};

type CfoReportingData = {
    totalInvoices?: CfoMetric | null;
    totalAmount?: CfoMetric | null;
    successRate?: CfoMetric | null;
    errorRate?: CfoMetric | null;
    timeTrend?: CfoTrendRow[] | null;
    statusBreakdown?: CfoStatusRow[] | null;
    countryBreakdown?: CfoCountryRow[] | null;
    currencyBreakdown?: CfoCurrencyRow[] | null;
    topErrorReasons?: CfoErrorReason[] | null;
    rejectedByRecipientEstimate?: number | null;
};

type CfoReportingQueryResult = {
    getCFOReporting?: CfoReportingData | null;
};

type CfoComplianceCacheEntry = {
    updatedAt: number;
    snapshot: CfoReportingData | null;
};

const DEFAULT_RANGE_DAYS = 30;
const LEGACY_CFO_COMPLIANCE_THEME_KEY = 'cfo-compliance-theme';
const CFO_COMPLIANCE_CACHE_PREFIX = 'cfoComplianceDashboardCache:v1';
const CFO_COMPLIANCE_CACHE_TTL_MS = 10 * 60 * 1000;

const toDateRange = (daysBack: number): [Dayjs, Dayjs] => {
    const end = dayjs();
    return [end.subtract(daysBack - 1, 'day').startOf('day'), end.endOf('day')];
};

const formatDelta = (value?: number | null): string => {
    const delta = Number(value ?? 0);
    const sign = delta >= 0 ? '+' : '';
    return `${sign}${delta.toFixed(1)}`;
};

const formatPercent = (value?: number | null): string => `${Number(value ?? 0).toFixed(1)}%`;

const formatCompactNumber = (value?: number | null): string => {
    const numberValue = Number(value ?? 0);
    if (Math.abs(numberValue) >= 1000000) {
        return `${(numberValue / 1000000).toFixed(2)}M`;
    }
    if (Math.abs(numberValue) >= 1000) {
        return `${(numberValue / 1000).toFixed(1)}K`;
    }
    return new Intl.NumberFormat().format(numberValue);
};

const formatMoney = (value?: number | null): string => {
    const numberValue = Number(value ?? 0);
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(numberValue);
};

const getStatusKey = (value?: string | null): string => String(value ?? '').trim().toLowerCase();

const getStatusColor = (value?: string | null): string => {
    const statusKey = getStatusKey(value);
    const statusColors: Record<string, string> = {
        delivered: '#22c55e',
        failed: '#ef4444',
        'in process': '#2563eb',
        'in transit': '#f59e0b',
        'business error': '#8b5cf6',
        errored: '#f97316',
        rejected: '#dc2626',
        cancelled: '#64748b',
        overdue: '#64748b',
        unknown: '#94a3b8',
    };
    return statusColors[statusKey] ?? '#94a3b8';
};

const getStatusLegendClass = (value?: string | null): string => {
    const statusKey = getStatusKey(value);
    if (statusKey === 'delivered') {
        return styles.statusDelivered;
    }
    if (statusKey === 'failed') {
        return styles.statusFailed;
    }
    if (statusKey === 'in process') {
        return styles.statusInProcess;
    }
    if (statusKey === 'in transit') {
        return styles.statusInTransit;
    }
    if (statusKey === 'business error') {
        return styles.statusBusinessError;
    }
    if (statusKey === 'overdue') {
        return styles.statusOverdue;
    }
    return styles.statusUnknown;
};

const getCountryDisplayName = (value?: string | null, displayNames?: Intl.DisplayNames | null): string => {
    const countryValue = String(value ?? '').trim();
    if (!countryValue) {
        return 'Unknown';
    }

    if (displayNames && /^[a-z]{2}$/i.test(countryValue)) {
        return displayNames.of(countryValue.toUpperCase()) ?? countryValue.toUpperCase();
    }

    return countryValue;
};

const CfoComplianceDashboard: FunctionComponent<IAppComponentProps> = (props) => {
    const intl = props.intl;
    const [theme] = useState<ThemeMode>(() => {
        const stored = globalThis.localStorage.getItem(EIM_THEME_STORAGE_KEY);
        if (stored === 'light' || stored === 'dark') {
            return stored;
        }

        const legacyStored = globalThis.localStorage.getItem(LEGACY_CFO_COMPLIANCE_THEME_KEY);
        if (legacyStored === 'light' || legacyStored === 'dark') {
            return legacyStored;
        }

        return stored === 'light' ? 'light' : 'dark';
    });
    const [selectedRange, setSelectedRange] = useState<[Dayjs, Dayjs]>(() => toDateRange(DEFAULT_RANGE_DAYS));
    const [cachedReportingData, setCachedReportingData] = useState<CfoReportingData | null>(null);
    const [getCfoReporting, { loading, data }] = useLazyQuery<CfoReportingQueryResult>(QUERY_CFO_REPORTING);

    const getCacheKey = useCallback((fromDate: Dayjs, toDate: Dayjs) => {
        const locale = globalThis.localStorage.getItem('locale') || 'en';
        return `${CFO_COMPLIANCE_CACHE_PREFIX}:${locale}:${fromDate.format('YYYY-MM-DD')}:${toDate.format('YYYY-MM-DD')}`;
    }, []);

    useEffect(() => {
        globalThis.localStorage.setItem(EIM_THEME_STORAGE_KEY, theme);
    }, [theme]);

    const loadComplianceData = useCallback(async (forceRefresh = false) => {
        const [fromDate, toDate] = selectedRange;
        const cacheKey = getCacheKey(fromDate, toDate);

        if (forceRefresh) {
            globalThis.sessionStorage.removeItem(cacheKey);
        }

        if (!forceRefresh) {
            const cached = globalThis.sessionStorage.getItem(cacheKey);
            if (cached) {
                try {
                    const parsed = JSON.parse(cached) as CfoComplianceCacheEntry;
                    const isFresh = Number.isFinite(parsed?.updatedAt)
                        && (Date.now() - parsed.updatedAt) <= CFO_COMPLIANCE_CACHE_TTL_MS;
                    if (isFresh) {
                        setCachedReportingData(parsed.snapshot ?? null);
                        return;
                    }
                    globalThis.sessionStorage.removeItem(cacheKey);
                } catch {
                    globalThis.sessionStorage.removeItem(cacheKey);
                }
            }
        }

        const response = await getCfoReporting({
            variables: {
                fromDate: fromDate.format('YYYY-MM-DD'),
                toDate: toDate.format('YYYY-MM-DD'),
            },
            fetchPolicy: 'network-only',
        });

        const snapshot = response?.data?.getCFOReporting ?? null;
        setCachedReportingData(snapshot);
        const cacheEntry: CfoComplianceCacheEntry = {
            updatedAt: Date.now(),
            snapshot,
        };
        globalThis.sessionStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
    }, [getCacheKey, getCfoReporting, selectedRange]);

    useEffect(() => {
        loadComplianceData().catch(console.error);
    }, [loadComplianceData]);

    const reportingData = data?.getCFOReporting ?? cachedReportingData ?? null;

    const totalInvoicesVal = Number(reportingData?.totalInvoices?.value ?? 0);
    const successRateVal = Number(reportingData?.successRate?.value ?? 0);
    const errorRateVal = Number(reportingData?.errorRate?.value ?? 0);

    const deliveredCount = Math.round((totalInvoicesVal * successRateVal) / 100);
    const errorsRejectedCount = Math.round((totalInvoicesVal * errorRateVal) / 100);

    const avgProcessingTime = 2.7;

    const complianceRate = successRateVal;

    const trendSeries = useMemo(() => {
        const rows = reportingData?.timeTrend ?? [];
        return {
            labels: rows.map((row) => dayjs(row.date ?? undefined).format('MMM D')),
            invoiceCount: rows.map((row) => Number(row.invoiceCount ?? 0)),
            totalAmount: rows.map((row) => Number(row.totalAmount ?? 0)),
        };
    }, [reportingData?.timeTrend]);

    const statusRows = useMemo(() => (reportingData?.statusBreakdown ?? []), [reportingData?.statusBreakdown]);
    const countryRows = useMemo(() => (reportingData?.countryBreakdown ?? []).slice(0, 5), [reportingData?.countryBreakdown]);
    const currencyRows = useMemo(() => (reportingData?.currencyBreakdown ?? []), [reportingData?.currencyBreakdown]);
    const countryDisplayNames = useMemo(() => {
        if (!Intl.DisplayNames) {
            return null;
        }
        return new Intl.DisplayNames(['en'], { type: 'region' });
    }, []);

    const trendOption = useMemo(() => ({
        grid: { top: 30, right: 20, bottom: 30, left: 50 },
        tooltip: { trigger: 'axis' },
        legend: {
            data: [
                getI18n('cfo-compliance-trend-invoices', 'eInvoices', intl),
            ],
            top: 0,
            textStyle: { color: theme === 'dark' ? '#cbd5e1' : '#334155' },
        },
        xAxis: {
            type: 'category',
            data: trendSeries.labels,
            axisLine: { lineStyle: { color: theme === 'dark' ? '#475569' : '#cbd5e1' } },
            axisLabel: { color: theme === 'dark' ? '#cbd5e1' : '#334155' },
        },
        yAxis: {
            type: 'value',
            name: getI18n('cfo-compliance-trend-y-invoices', 'eInvoices', intl),
            axisLabel: { color: theme === 'dark' ? '#cbd5e1' : '#334155' },
            splitLine: { lineStyle: { color: theme === 'dark' ? '#24324a' : '#e2e8f0' } },
        },
        series: [
            {
                name: getI18n('cfo-compliance-trend-invoices', 'eInvoices', intl),
                type: 'line',
                smooth: true,
                symbolSize: 6,
                lineStyle: { color: '#2563eb', width: 3 },
                itemStyle: { color: '#2563eb' },
                areaStyle: { color: 'rgba(37, 99, 235, 0.12)' },
                data: trendSeries.invoiceCount,
            },
        ],
    }), [intl, theme, trendSeries]);

    const statusOption = useMemo(() => {
        const totalCount = statusRows.reduce((sum, row) => sum + Number(row.count ?? 0), 0);
        return {
            tooltip: {
                trigger: 'item',
                formatter: '{b}: {c} ({d}%)',
            },
            series: [{
                type: 'pie',
                radius: ['58%', '78%'],
                center: ['50%', '52%'],
                avoidLabelOverlap: false,
                label: {
                    show: true,
                    position: 'center',
                    formatter: () => `${formatCompactNumber(totalCount)}\n${getI18n('cfo-compliance-total', 'Total', intl)}`,
                    fontSize: 18,
                    fontWeight: 700,
                    color: theme === 'dark' ? '#e2e8f0' : '#0f172a',
                },
                data: statusRows.map((row) => ({
                    value: Number(row.count ?? 0),
                    name: row.status ?? getI18n('cfo-compliance-status-unknown', 'Unknown', intl),
                    itemStyle: {
                        color: getStatusColor(row.status),
                    },
                })),
            }],
        };
    }, [intl, statusRows, theme]);

    const countryOption = useMemo(() => ({
        grid: { top: 10, right: 70, bottom: 10, left: 100 },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: (params: any) => {
                const rowIndex = Array.isArray(params) ? params[0]?.dataIndex : params?.dataIndex;
                const row = countryRows[rowIndex] ?? null;
                if (!row) {
                    return '';
                }

                const countryName = getCountryDisplayName(row.country, countryDisplayNames);
                const flagSrc = getCountryFlagUrl(row.country);
                const value = Number(row.invoiceCount ?? 0);
                const percent = Number(row.percentage ?? 0);
                const flagMarkup = flagSrc
                    ? `<img src="${flagSrc}" alt="${escapeHtml(countryName)}" width="20" height="20" style="display:block;height:20px;object-fit:cover;width:20px;" onerror="this.style.display='none';" />`
                    : '';

                return `
                    <div style="align-items:center;display:flex;gap:8px;font-weight:700;margin-bottom:6px;">
                        <span style="align-items:center;background:#fff;border:1px solid rgba(148,163,184,0.35);border-radius:999px;display:inline-flex;height:20px;justify-content:center;overflow:hidden;width:20px;">${flagMarkup}</span>
                        <span>${escapeHtml(countryName)}</span>
                    </div>
                    <div>${escapeHtml(getI18n('cfo-compliance-country-tooltip-invoices', 'Invoices', intl))}: ${formatCompactNumber(value)}</div>
                    <div>${escapeHtml(getI18n('cfo-compliance-country-tooltip-share', 'Share', intl))}: ${formatPercent(percent)}</div>
                `;
            },
        },
        xAxis: {
            type: 'value',
            axisLabel: { show: false },
            splitLine: { show: false },
        },
        yAxis: {
            type: 'category',
            data: countryRows.map((row) => getCountryDisplayName(row.country, countryDisplayNames)),
            axisLabel: {
                color: theme === 'dark' ? '#cbd5e1' : '#0f172a',
                fontWeight: 600,
                fontSize: 12,
            },
            axisLine: { show: false },
            axisTick: { show: false },
        },
        series: [{
            type: 'bar',
            data: countryRows.map((row) => Number(row.invoiceCount ?? 0)),
            barWidth: 16,
            itemStyle: { color: '#2563eb', borderRadius: [0, 999, 999, 0] },
            label: {
                show: true,
                position: 'right',
                color: theme === 'dark' ? '#e2e8f0' : '#0f172a',
                fontWeight: 600,
                fontSize: 12,
                formatter: ({ value, dataIndex }: { value: number; dataIndex: number }) => {
                    const percentage = Number(countryRows[dataIndex]?.percentage ?? 0);
                    return `${formatPercent(percentage)}`;
                },
            },
        }],
    }), [countryRows, countryDisplayNames, theme]);

    const gaugeOption = useMemo(() => ({
        series: [{
            type: 'gauge',
            startAngle: 180,
            endAngle: 0,
            min: 0,
            max: 100,
            center: ['50%', '70%'],
            radius: '110%',
            splitNumber: 10,
            axisLine: {
                lineStyle: {
                    width: 16,
                    color: [
                        [0.3, '#ef4444'],
                        [0.7, '#f59e0b'],
                        [1, '#22c55e'],
                    ],
                },
            },
            pointer: {
                itemStyle: { color: theme === 'dark' ? '#cbd5e1' : '#334155' },
                length: '60%',
                width: 6,
            },
            axisTick: { show: false },
            splitLine: { show: false },
            axisLabel: {
                distance: -28,
                color: theme === 'dark' ? '#94a3b8' : '#64748b',
                fontSize: 10,
                formatter: (value: number) => {
                    if (value === 0 || value === 100) {
                        return `${value}%`;
                    }
                    return '';
                },
            },
            detail: {
                valueAnimation: true,
                formatter: '{value}%',
                color: theme === 'dark' ? '#e2e8f0' : '#0f172a',
                fontSize: 28,
                fontWeight: 700,
                offsetCenter: [0, '-10%'],
            },
            title: {
                show: true,
                offsetCenter: [0, '40%'],
                color: theme === 'dark' ? '#94a3b8' : '#64748b',
                fontSize: 13,
                fontWeight: 600,
            },
            data: [{ value: complianceRate, name: getI18n('cfo-compliance-rate-label', 'Compliance Rate', intl) }],
        }],
    }), [complianceRate, intl, theme]);

    const handleRefresh = useCallback(() => {
        loadComplianceData(true).catch(console.error);
    }, [loadComplianceData]);

    const exportCurrentView = () => {
        const payload = {
            range: selectedRange.map((value) => value.format('YYYY-MM-DD')),
            data: reportingData,
        };
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
        const link = globalThis.document.createElement('a');
        link.href = globalThis.URL.createObjectURL(blob);
        link.download = 'cfo-compliance-report.json';
        link.click();
        globalThis.URL.revokeObjectURL(link.href);
    };

    const alertRows = useMemo(() => {
        const statusCountByName = statusRows.reduce<Record<string, number>>((accumulator, row) => {
            const statusKey = getStatusKey(row.status);
            const count = Number(row.count ?? 0);
            accumulator[statusKey] = (accumulator[statusKey] ?? 0) + count;
            return accumulator;
        }, {});

        const invoicesWithErrors = (statusCountByName.failed ?? 0) + (statusCountByName.overdue ?? 0);
        const overdueInvoices = statusCountByName.overdue ?? 0;
        const rejectedByRecipients = Number(reportingData?.rejectedByRecipientEstimate ?? 0);

        return [
            {
                icon: <ExclamationCircleOutlined style={{ color: '#ef4444', fontSize: 18 }} />,
                label: getI18n('cfo-compliance-alert-errors', 'Invoices with errors', intl),
                count: invoicesWithErrors,
            },
            {
                icon: <ClockCircleOutlined style={{ color: '#f59e0b', fontSize: 18 }} />,
                label: getI18n('cfo-compliance-alert-overdue', 'Overdue eInvoices', intl),
                count: overdueInvoices,
            },
            {
                icon: <ExclamationCircleOutlined style={{ color: '#2563eb', fontSize: 18 }} />,
                label: getI18n('cfo-compliance-alert-rejected', 'Rejected by recipients', intl),
                count: rejectedByRecipients,
            },
        ];
    }, [intl, reportingData?.rejectedByRecipientEstimate, statusRows]);

    return (
        <Spin spinning={loading} size="large">
            <div className={classNames(styles.page, {
                [styles.pageLight]: theme === 'light',
                [styles.pageDark]: theme === 'dark',
            })}>
                <div className={styles.shell}>
                    <div className={styles.topBar}>
                        <div className={styles.titleBlock}>
                            <h1 className={styles.pageTitle}>{getI18n('cfo-compliance-title', 'eInvoice Reporting', intl)}</h1>
                            <p className={styles.pageSubtitle}>{getI18n('cfo-compliance-subtitle', 'Overview of eInvoice volume, status and compliance.', intl)}</p>
                        </div>
                        <div className={styles.controlsBlock}>
                            <div className={styles.rangePicker}>
                                <RangePicker
                                    value={selectedRange}
                                    onChange={(values) => {
                                        if (values?.[0] && values?.[1]) {
                                            setSelectedRange([values[0], values[1]]);
                                        }
                                    }}
                                    allowClear={false}
                                    format="MMM D, YYYY"
                                    locale={getRangePickerLocalization(intl, globalThis.localStorage.getItem('locale') || 'en')}
                                />
                            </div>
                            <Button icon={<DownloadOutlined />} onClick={exportCurrentView}>
                                {getI18n('cfo-compliance-export', 'Export', intl)}
                            </Button>
                            <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
                                {getI18n('cfo-reporting-refresh', 'Refresh', intl)}
                            </Button>
                        </div>
                    </div>

                    <div className={styles.kpiGrid}>
                        <Card className={styles.kpiCard} bordered={false}>
                            <div className={styles.kpiLabel}>{getI18n('cfo-compliance-kpi-total', 'Total eInvoices', intl)}</div>
                            <div className={styles.kpiValue}>{formatCompactNumber(totalInvoicesVal)}</div>
                            <div className={classNames(styles.kpiTrend, {
                                [styles.kpiTrendPositive]: (reportingData?.totalInvoices?.percentageChange ?? 0) >= 0,
                                [styles.kpiTrendNegative]: (reportingData?.totalInvoices?.percentageChange ?? 0) < 0,
                            })}>
                                {(reportingData?.totalInvoices?.percentageChange ?? 0) >= 0 ? '▲' : '▼'} {formatDelta(reportingData?.totalInvoices?.percentageChange)}% {getI18n('cfo-compliance-vs-previous', 'vs. previous month', intl)}
                            </div>
                            <div className={styles.kpiIcon}>
                                <FileTextOutlined />
                            </div>
                        </Card>

                        <Card className={styles.kpiCard} bordered={false}>
                            <div className={styles.kpiLabel}>{getI18n('cfo-compliance-kpi-currencies', 'Currencies Tracked', intl)}</div>
                            <div className={styles.kpiValue}>{formatCompactNumber(currencyRows.length)}</div>
                            <div className={styles.kpiTrend}>
                                {getI18n('cfo-compliance-kpi-currencies-helper', 'Total amount breakdown is shown by currency below.', intl)}
                            </div>
                            <div className={styles.kpiIcon}>
                                <DollarOutlined />
                            </div>
                        </Card>

                        <Card className={styles.kpiCard} bordered={false}>
                            <div className={styles.kpiLabel}>{getI18n('cfo-compliance-kpi-delivered', 'Successfully Delivered', intl)}</div>
                            <div className={styles.kpiValue}>{formatCompactNumber(deliveredCount)} <span className={styles.kpiPercentage}>({formatPercent(successRateVal)})</span></div>
                            <div className={classNames(styles.kpiTrend, {
                                [styles.kpiTrendPositive]: (reportingData?.successRate?.percentageChange ?? 0) >= 0,
                                [styles.kpiTrendNegative]: (reportingData?.successRate?.percentageChange ?? 0) < 0,
                            })}>
                                {(reportingData?.successRate?.percentageChange ?? 0) >= 0 ? '▲' : '▼'} {formatDelta(reportingData?.successRate?.percentageChange)}% {getI18n('cfo-compliance-vs-previous', 'vs. previous month', intl)}
                            </div>
                            <div className={styles.kpiIcon}>
                                <CheckCircleOutlined />
                            </div>
                        </Card>

                        <Card className={styles.kpiCard} bordered={false}>
                            <div className={styles.kpiLabel}>{getI18n('cfo-compliance-kpi-errors', 'Errors / Rejected', intl)}</div>
                            <div className={styles.kpiValue}>{formatCompactNumber(errorsRejectedCount)} <span className={styles.kpiPercentage}>({formatPercent(errorRateVal)})</span></div>
                            <div className={classNames(styles.kpiTrend, {
                                [styles.kpiTrendPositive]: (reportingData?.errorRate?.percentageChange ?? 0) <= 0,
                                [styles.kpiTrendNegative]: (reportingData?.errorRate?.percentageChange ?? 0) > 0,
                            })}>
                                {(reportingData?.errorRate?.percentageChange ?? 0) >= 0 ? '▲' : '▼'} {formatDelta(reportingData?.errorRate?.percentageChange)}% {getI18n('cfo-compliance-vs-previous', 'vs. previous month', intl)}
                            </div>
                            <div className={styles.kpiIcon}>
                                <ExclamationCircleOutlined />
                            </div>
                        </Card>

                        <Card className={styles.kpiCard} bordered={false}>
                            <div className={styles.kpiLabel}>{getI18n('cfo-compliance-kpi-processing-time', 'Avg. Processing Time', intl)}</div>
                            <div className={styles.kpiValue}>{avgProcessingTime.toFixed(1)} <span className={styles.kpiUnit}>{getI18n('cfo-compliance-unit-days', 'days', intl)}</span></div>
                            <div className={classNames(styles.kpiTrend, styles.kpiTrendPositive)}>
                                ▼ -0.4 {getI18n('cfo-compliance-unit-days', 'days', intl)} {getI18n('cfo-compliance-vs-previous', 'vs. previous month', intl)}
                            </div>
                            <div className={styles.kpiIcon}>
                                <ClockCircleOutlined />
                            </div>
                        </Card>
                    </div>

                    <div className={styles.mainGrid}>
                        <Card className={styles.chartCard} bordered={false}>
                            <div className={styles.cardTitle}>{getI18n('cfo-compliance-trend-title', 'eInvoices Over Time', intl)}</div>
                            <ReactEcharts className={styles.trendChart} option={trendOption} />
                        </Card>
                        <Card className={styles.chartCard} bordered={false}>
                            <div className={styles.cardTitle}>{getI18n('cfo-compliance-status-title', 'eInvoices by Status', intl)}</div>
                            <ReactEcharts className={styles.statusChart} option={statusOption} />
                            <div className={styles.statusLegend}>
                                {statusRows.slice(0, 5).map((row, index) => (
                                    <div className={styles.legendRow} key={`${row.status}-${index}`}>
                                        <span className={classNames(styles.legendDot, getStatusLegendClass(row.status))} />
                                        <span className={styles.legendLabel}>{row.status ?? getI18n('cfo-compliance-status-unknown', 'Unknown', intl)}</span>
                                        <span className={styles.legendCount}>{formatCompactNumber(row.count)} ({formatPercent(row.percentage)})</span>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </div>

                    <div className={styles.bottomGrid}>
                        <Card className={styles.chartCard} bordered={false}>
                            <div className={styles.cardTitle}>{getI18n('cfo-reporting-currency-breakdown-title', 'Total Amount by Currency', intl)}</div>
                            <div className={styles.currencyTableWrapper}>
                                <table className={styles.currencyTable}>
                                    <thead>
                                        <tr>
                                            <th>{getI18n('cfo-reporting-currency-col-currency', 'Currency', intl)}</th>
                                            <th>{getI18n('cfo-reporting-currency-col-current', 'Current Period', intl)}</th>
                                            <th>{getI18n('cfo-reporting-currency-col-previous', 'Previous month', intl)}</th>
                                            <th>{getI18n('cfo-reporting-currency-col-delta', 'Change', intl)}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currencyRows.length > 0 ? currencyRows.map((row, index) => {
                                            const delta = Number(row.delta ?? 0);
                                            const isPositive = delta >= 0;

                                            return (
                                                <tr key={`${row.currency ?? 'UNKNOWN'}-${index}`}>
                                                    <td>{String(row.currency ?? 'UNKNOWN')}</td>
                                                    <td>{formatMoney(row.currentAmount)}</td>
                                                    <td>{formatMoney(row.previousAmount)}</td>
                                                    <td className={classNames(styles.currencyDelta, isPositive ? styles.currencyDeltaPositive : styles.currencyDeltaNegative)}>
                                                        {isPositive ? '+' : ''}{formatMoney(delta)}
                                                    </td>
                                                </tr>
                                            );
                                        }) : (
                                            <tr>
                                                <td colSpan={4} className={styles.currencyEmpty}>
                                                    {getI18n('cfo-reporting-currency-empty', 'No currency amount data for this period.', intl)}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </Card>

                        <Card className={styles.chartCard} bordered={false}>
                            <div className={styles.cardTitle}>{getI18n('cfo-compliance-errors-title', 'Top Error Reasons', intl)}</div>
                            <div className={styles.tableWrapper}>
                                <table className={styles.compactTable}>
                                    <thead>
                                        <tr>
                                            <th>{getI18n('cfo-compliance-col-reason', 'Reason', intl)}</th>
                                            <th>{getI18n('cfo-compliance-col-invoices', 'eInvoices', intl)}</th>
                                            <th>{getI18n('cfo-compliance-col-share', 'Share', intl)}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(reportingData?.topErrorReasons ?? []).map((row) => (
                                            <tr key={`${row.reason ?? 'unknown'}-${row.count ?? 0}-${row.percentage ?? 0}`}>
                                                <td>{row.reason ?? getI18n('cfo-compliance-status-unknown', 'Unknown', intl)}</td>
                                                <td>{formatCompactNumber(row.count ?? 0)}</td>
                                                <td>{(row.percentage ?? 0).toFixed(1)}%</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    </div>

                    <div className={styles.finalGrid}>
                        <Card className={styles.chartCard} bordered={false}>
                            <div className={styles.cardTitle}>{getI18n('cfo-compliance-rate-title', 'Compliance Rate', intl)}</div>
                            <ReactEcharts className={styles.gaugeChart} option={gaugeOption} />
                        </Card>

                        <Card className={styles.chartCard} bordered={false}>
                            <div className={styles.cardTitle}>{getI18n('cfo-compliance-country-title', 'eInvoicing by Country (Top 5)', intl)}</div>
                            <ReactEcharts className={styles.countryChart} option={countryOption} />
                        </Card>

                        <Card className={styles.chartCard} bordered={false}>
                            <div className={styles.cardTitle}>{getI18n('cfo-compliance-alerts-title', 'Alerts & Notifications', intl)}</div>
                            <div className={styles.alertList}>
                                {alertRows.map((alert) => (
                                    <div className={styles.alertRow} key={alert.label}>
                                        <div className={styles.alertIcon}>{alert.icon}</div>
                                        <div className={styles.alertLabel}>{alert.label}</div>
                                        <div className={styles.alertCount}>{formatCompactNumber(alert.count)}</div>
                                        <div className={styles.alertArrow}>›</div>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </div>
                </div>
            </div>
        </Spin>
    );
};

export default CfoComplianceDashboard;
